import React from 'react';
import { flushSync } from 'react-dom';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Button, Field, TextArea } from '@/nova/components';
import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';
import { useAppDispatch, useAppSelector } from '@/store';
import { kitRenderCostFor } from '@/store/slices/aiCredits';
import { fetchOwnerWallets } from '@/store/slices/wallet';

import {
  editKitLayer,
  flattenKitLayers,
  type AdApiFailure,
  type BannerKitVersion,
  type BannerKitVersionEntry,
  type KitLayer,
  type KitLayerText,
} from '../../banner-kit-api';
import type { MintedVersion } from '../../hooks/useAdVersions';
import {
  DEFAULT_LAYER_TRANSFORM,
  MAX_LAYER_SCALE,
  MIN_LAYER_SCALE,
  clampLayerScale,
  dragTransform,
  flattenedVersion,
  isRestylableRole,
  isTextLayerRole,
  layerImagesChanged,
  layerReadOf,
  layerStageStyle,
  layersEntry,
  moveLayer,
  normaliseLayerItems,
  nudgeTransform,
  stageScale,
  transformsChanged,
  transformsFor,
  withLayerImage,
  withTransform,
  zOrderChanged,
  type LayerSize,
} from '../../layer-transforms';
import { GenerateButton } from '../GenerateButton';
import { LayerList, layerRoleLabel } from './LayerList';
import * as S from './KitLayerEditor.styles';

export interface KitLayerEditorProps {
  tenantId: number;
  kitId: string;
  /** The version whose layers are edited, as stored (no view-only keys): its `shapes[shape].layers` exist. */
  version: BannerKitVersion;
  /** The key under `version.shapes` of the decomposed render. */
  shape: string;
  /** The size the editor was opened from, for the title ("728 × 90"). */
  sizeText: string;
  /** The other sizes cut from the same render (they change with it). */
  siblingSizes: string[];
  /** The render size when the render does not carry it (the catalogue shape's). */
  renderSize?: LayerSize;
  /** The kit's image model (the price of a text change or restyle). */
  model: string;
  /** Keep edited layers as a NEW unlocked version (selected in the composer); null when it cannot be minted. */
  onSnapshot: (entry: BannerKitVersionEntry) => MintedVersion | null;
  /** The flatten wrote a new generated version: show and select it. */
  onFlattened: (version: BannerKitVersion) => void;
  /** Whether the version's document is stored as shown: the backend builds the new version from the stored
   *  document, so a save waits for it and stops when it is not. */
  documentStored?: () => Promise<boolean>;
  onBuyCredits: (needed?: number) => void;
  onNotify: (message: string) => void;
  onClose: () => void;
}

/** The tallest the stage grows (a skyscraper stays on screen); its width follows the column. A layout constant. */
const STAGE_MAX_H = 460;

/** The version the backend reads layers from: the one opened, or the last snapshot this editor saved. */
interface LayerSource {
  at: string;
  persisted: Promise<boolean>;
  /** The layers as saved on that version (the flatten takes transforms, not the stacking order or images). */
  items: KitLayer[];
}

type Busy = 'edit' | 'save' | null;

/** An owner-readable reason a text change or restyle was refused. Pure. */
export const layerEditErrorText = (res: AdApiFailure, t: TFunction): string => {
  if (res.status === 409 || /not available/i.test(res.error ?? '')) {
    return t('Layer editing is not available in this environment yet.');
  }
  if (res.status === 404) return t("That version couldn't be found. Close the banner and open it again.");
  // A 200 with `ok: false`: the edit ran and failed, and the backend refunded it.
  if (res.status === 200) return t("Lumio couldn't change that layer. Your credits were refunded. Please try again.");
  return t("That layer couldn't be changed. Please try again.");
};

/** An owner-readable reason the save (flatten) was refused. Pure. */
export const layerSaveErrorText = (res: AdApiFailure, t: TFunction): string => {
  if (res.status === 409 || /not available/i.test(res.error ?? '')) {
    return t('Layer editing is not available in this environment yet.');
  }
  if (res.status === 404) return t("That version couldn't be found. Close the banner and open it again.");
  return t("The layers couldn't be saved. Please try again.");
};

/**
 * The kit layer editor (desktop only): one shape render separated into its layers (logo, headline, sub-line, call to
 * action, subject, decorations) over the inpainted base. Drag a layer to move it, size it with the slider, hide it,
 * or change its place in the stack; all of that is free and kept in the editor until Save. A text layer can be
 * rewritten and a layer restyled with Lumio (priced as one render each); the new layer image is kept at once as a
 * new version, so a paid result never depends on pressing Save. Save composites the base and the edited layers into
 * the shape's new render on a new generated version, which the composer then selects.
 */
export const KitLayerEditor = ({
  tenantId,
  kitId,
  version,
  shape,
  sizeText,
  siblingSizes,
  renderSize,
  model,
  onSnapshot,
  onFlattened,
  documentStored,
  onBuyCredits,
  onNotify,
  onClose,
}: KitLayerEditorProps): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const pricing = useAppSelector((state) => state.aiCredits?.pricing);
  const { closeHandle, dismiss } = useSheetDismiss(onClose);

  // The callbacks through a ref: an async edit or save calls the ones of the latest render.
  const propsRef = React.useRef({ onSnapshot, onFlattened, documentStored, onBuyCredits, onNotify });
  propsRef.current = { onSnapshot, onFlattened, documentStored, onBuyCredits, onNotify };
  const liveRef = React.useRef(true);
  React.useEffect(
    () => () => {
      liveRef.current = false;
    },
    [],
  );

  /* ---------- the layers ---------- */

  // The version and its layers as opened: the editor works on this snapshot, whatever the composer selects meanwhile.
  const [opened] = React.useState(() => ({
    version,
    items: normaliseLayerItems(version.shapes[shape]?.layers?.items),
  }));
  const layers = opened.version.shapes[shape]?.layers;
  const [items, setItems] = React.useState<KitLayer[]>(opened.items);
  const itemsRef = React.useRef(items);
  const update = (next: KitLayer[]): void => {
    itemsRef.current = next;
    setItems(next);
  };
  const sourceRef = React.useRef<LayerSource>({
    at: opened.version.created_at,
    persisted: Promise.resolve(true),
    items: opened.items,
  });

  const [selectedId, setSelectedId] = React.useState<string | undefined>(undefined);
  const selected = items.find((item) => item.id === selectedId);
  const [textDraft, setTextDraft] = React.useState('');
  const [restyleDraft, setRestyleDraft] = React.useState('');
  const [busy, setBusy] = React.useState<Busy>(null);
  const locked = busy !== null;

  const selectLayer = (id: string | undefined): void => {
    if (id === selectedId) return;
    setSelectedId(id);
    const item = itemsRef.current.find((x) => x.id === id);
    setTextDraft(item?.text?.intended ?? '');
    setRestyleDraft('');
  };

  /* ---------- the stage ---------- */

  const wrapRef = React.useRef<HTMLDivElement>(null);
  const [availW, setAvailW] = React.useState(0);
  // Syncs the stage size with the column's width (the sheet and the viewport can resize).
  React.useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const pad = (): number => {
      const cs = window.getComputedStyle(el);
      return parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
    };
    if (typeof ResizeObserver === 'undefined') {
      setAvailW(Math.floor(el.clientWidth - pad()));
      return undefined;
    }
    const ro = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width !== undefined) setAvailW(Math.floor(width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const [baseNatural, setBaseNatural] = React.useState<LayerSize | undefined>(undefined);
  const [naturals, setNaturals] = React.useState<Record<string, LayerSize>>({});
  const stored = opened.version.shapes[shape];
  const render: LayerSize =
    stored?.w && stored?.h
      ? { w: stored.w, h: stored.h }
      : renderSize && renderSize.w > 0 && renderSize.h > 0
        ? renderSize
        : baseNatural ?? { w: 0, h: 0 };
  const s = stageScale(render, { w: availW, h: STAGE_MAX_H });
  const stageW = Math.round(render.w * s);
  const stageH = Math.round(render.h * s);

  const dragRef = React.useRef<{
    id: string;
    pointerId: number;
    x: number;
    y: number;
    origin: KitLayer['transform'];
  } | null>(null);

  const onLayerPointerDown = (e: React.PointerEvent<HTMLImageElement>, item: KitLayer): void => {
    if (locked) return;
    e.preventDefault();
    e.stopPropagation();
    selectLayer(item.id);
    if (item.transform.hidden || !(s > 0)) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { id: item.id, pointerId: e.pointerId, x: e.clientX, y: e.clientY, origin: item.transform };
  };

  const onLayerPointerMove = (e: React.PointerEvent<HTMLImageElement>): void => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId || locked) return;
    const item = itemsRef.current.find((x) => x.id === drag.id);
    if (!item) return;
    const next = dragTransform(drag.origin, { x: e.clientX - drag.x, y: e.clientY - drag.y }, s, item.box_px, render);
    update(withTransform(itemsRef.current, drag.id, next));
  };

  const endDrag = (e: React.PointerEvent<HTMLImageElement>): void => {
    if (dragRef.current?.pointerId === e.pointerId) dragRef.current = null;
  };

  const ARROWS: Record<string, [number, number]> = {
    ArrowLeft: [-1, 0],
    ArrowRight: [1, 0],
    ArrowUp: [0, -1],
    ArrowDown: [0, 1],
  };
  // The arrow keys nudge the selected layer by one stage pixel (ten with Shift), for a precise placement.
  const onStageKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const dir = ARROWS[e.key];
    if (!dir || locked || !selected || selected.transform.hidden || !(s > 0)) return;
    e.preventDefault();
    const step = Math.max(1, Math.round((e.shiftKey ? 10 : 1) / s));
    const next = nudgeTransform(selected.transform, { x: dir[0] * step, y: dir[1] * step }, selected.box_px, render);
    update(withTransform(itemsRef.current, selected.id, next));
  };

  /* ---------- free edits ---------- */

  const setTransform = (id: string, patch: Partial<KitLayer['transform']>): void => {
    if (locked) return;
    update(withTransform(itemsRef.current, id, patch));
  };
  const toggleHidden = (id: string): void => {
    const item = itemsRef.current.find((x) => x.id === id);
    if (item) setTransform(id, { hidden: !item.transform.hidden });
  };
  const move = (id: string, direction: 'forward' | 'backward'): void => {
    if (locked) return;
    update(moveLayer(itemsRef.current, id, direction));
  };

  /* ---------- paid edits (a text change or a restyle) ---------- */

  /** Keep the layers as they are now on a new version and read later layer edits from it. */
  const snapshot = (next: KitLayer[]): MintedVersion | null => {
    const minted = propsRef.current.onSnapshot(layersEntry(opened.version, shape, next));
    if (minted) sourceRef.current = { at: minted.at, persisted: minted.persisted, items: next };
    return minted;
  };

  const editCost = kitRenderCostFor(pricing, model);

  const runEdit = async (kind: 'text' | 'restyle'): Promise<void> => {
    const item = selected;
    const value = (kind === 'text' ? textDraft : restyleDraft).trim();
    if (!item || !value || locked) return;
    setBusy('edit');
    try {
      // The backend reads the layer from the version: a snapshot this editor just minted must be saved first.
      if (!(await sourceRef.current.persisted)) {
        propsRef.current.onNotify(t("Your changes couldn't be saved. Please try again."));
        return;
      }
      const res = await editKitLayer(tenantId, {
        kit_id: kitId,
        target_version_at: sourceRef.current.at,
        shape,
        layer_id: item.id,
        ...(kind === 'text' ? { text: value } : { instruction: value }),
      });
      if (!liveRef.current) return;
      if (!res.ok) {
        if (res.aborted) return;
        if (res.status === 402) propsRef.current.onBuyCredits(res.needed);
        else propsRef.current.onNotify(layerEditErrorText(res, t));
        return;
      }
      void dispatch(fetchOwnerWallets());
      // A text change records the new words and their read-back. A restyle of a text layer is read back against the
      // words it keeps, so its record follows that read too: a restyle that garbled the words is flagged, and one
      // that fixed them is not.
      const intended = kind === 'text' ? value : res.data.qa ? item.text?.intended : undefined;
      const read = layerReadOf(res.data.qa?.read, item.role);
      const text: KitLayerText | undefined = intended
        ? {
            intended,
            ...(read !== undefined ? { read } : {}),
            ...(res.data.qa ? { verified: res.data.qa.status === 'ok' } : {}),
          }
        : undefined;
      const next = withLayerImage(itemsRef.current, item.id, res.data.url, text);
      update(next);
      if (kind === 'restyle') setRestyleDraft('');
      // The paid result is kept right away (a version is never rewritten in place, so this is a new one).
      const minted = snapshot(next);
      if (!minted) {
        propsRef.current.onNotify(t("The new layer couldn't be kept. Save now to keep it."));
        return;
      }
      void minted.persisted.then((ok) => {
        if (!ok && liveRef.current)
          propsRef.current.onNotify(t("The new layer couldn't be kept. Save now to keep it."));
      });
    } finally {
      if (liveRef.current) setBusy(null);
    }
  };

  /* ---------- save (flatten) ---------- */

  const dirty =
    transformsChanged(opened.items, items) ||
    zOrderChanged(opened.items, items) ||
    layerImagesChanged(opened.items, items);

  const save = async (): Promise<void> => {
    if (locked || !dirty) return;
    setBusy('save');
    let saved = false;
    try {
      const current = itemsRef.current;
      // The flatten takes each layer's transform but not the stacking order or new images: a version must carry
      // those first.
      if (zOrderChanged(sourceRef.current.items, current) || layerImagesChanged(sourceRef.current.items, current)) {
        if (!snapshot(current)) {
          propsRef.current.onNotify(t("The layers couldn't be saved. Please try again."));
          return;
        }
      }
      const source = sourceRef.current;
      if (propsRef.current.documentStored && !(await propsRef.current.documentStored())) {
        if (liveRef.current) propsRef.current.onNotify(t("Your changes couldn't be saved. Please try again."));
        return;
      }
      if (!(await source.persisted)) {
        if (liveRef.current) propsRef.current.onNotify(t("Your changes couldn't be saved. Please try again."));
        return;
      }
      const res = await flattenKitLayers(tenantId, kitId, {
        version_at: source.at,
        shape,
        transforms: transformsFor(current),
      });
      if (!liveRef.current) return;
      if (!res.ok) {
        if (!res.aborted) propsRef.current.onNotify(layerSaveErrorText(res, t));
        return;
      }
      if (!res.data.version_at || !res.data.url) {
        propsRef.current.onNotify(t("The layers couldn't be saved. Please try again."));
        return;
      }
      propsRef.current.onFlattened(flattenedVersion(opened.version, shape, current, res.data.version_at, res.data.url));
      propsRef.current.onNotify(t('Saved as a new version.'));
      saved = true;
    } finally {
      // Leave the busy state in this frame, so the sheet (which refuses to close while busy) can close right after.
      if (liveRef.current) flushSync(() => setBusy(null));
    }
    if (saved) dismiss();
  };

  /* ---------- render ---------- */

  const role = selected ? layerRoleLabel(selected.role, t) : '';
  const scale = selected ? clampLayerScale(selected.transform.scale) : 1;
  const untouched =
    !selected ||
    (selected.transform.dx === DEFAULT_LAYER_TRANSFORM.dx &&
      selected.transform.dy === DEFAULT_LAYER_TRANSFORM.dy &&
      scale === DEFAULT_LAYER_TRANSFORM.scale);
  const textValue = textDraft.trim();

  const status =
    busy === 'edit' ? (
      <S.FootText text="Lumio is redrawing the layer…" />
    ) : busy === 'save' ? (
      <S.FootText text="Saving a new version…" />
    ) : dirty ? (
      <S.FootText text="Moves, sizes, order and visibility are kept when you save." />
    ) : (
      <S.FootText text="Save creates a new version of the banner with this size redrawn from its layers." />
    );

  return (
    <Sheet
      title={t('Edit layers · {{size}}', { size: sizeText })}
      onClose={onClose}
      closeHandle={closeHandle}
      busy={locked}
      maxWidth="1100px"
      maxHeight="100%"
      footer={
        <S.Foot>
          <S.FootStatus>{status}</S.FootStatus>
          <S.FootActions>
            <Button variant="text" label={t('Close')} onClick={dismiss} disabled={locked} />
            <Button
              label={busy === 'save' ? t('Saving…') : t('Save as new version')}
              icon="check"
              onClick={() => void save()}
              disabled={locked || !dirty}
            />
          </S.FootActions>
        </S.Foot>
      }
    >
      <S.Body>
        <S.StageWrap ref={wrapRef}>
          {layers && s > 0 ? (
            <S.Stage
              $w={stageW}
              $h={stageH}
              tabIndex={0}
              role="group"
              aria-label={t('Layer canvas. Drag a layer to move it; the arrow keys nudge the selected one.')}
              onKeyDown={onStageKeyDown}
              onPointerDown={() => {
                if (!locked) selectLayer(undefined);
              }}
            >
              <S.StageBase src={layers.base_url} alt="" draggable={false} />
              {items.map((item) => {
                const isSelected = item.id === selectedId;
                const hidden = item.transform.hidden;
                return (
                  <S.LayerImg
                    key={item.id}
                    src={item.url}
                    alt={layerRoleLabel(item.role, t)}
                    draggable={false}
                    $place={layerStageStyle(item, s, render, naturals[item.id], baseNatural)}
                    $z={item.z}
                    $opacity={hidden ? (isSelected ? 0.25 : 0) : 1}
                    $selected={isSelected}
                    $grabbable={!hidden && !locked}
                    onLoad={(e) => {
                      const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                      setNaturals((cur) =>
                        cur[item.id]?.w === w && cur[item.id]?.h === h ? cur : { ...cur, [item.id]: { w, h } },
                      );
                    }}
                    onPointerDown={(e) => onLayerPointerDown(e, item)}
                    onPointerMove={onLayerPointerMove}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                  />
                );
              })}
            </S.Stage>
          ) : (
            <S.StageNote text="Loading the layers…" />
          )}
          {layers && !baseNatural ? (
            // Measures the base image (a render without a stored size is sized from it). Never shown.
            <img
              src={layers.base_url}
              alt=""
              hidden
              onLoad={(e) => setBaseNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
            />
          ) : null}
        </S.StageWrap>

        <S.Panel>
          <S.Section>
            <S.SectionTitle text="Layers" />
            {items.length > 0 ? (
              <LayerList
                items={items}
                selectedId={selectedId}
                disabled={locked}
                onSelect={(id) => {
                  if (!locked) selectLayer(id);
                }}
                onToggleHidden={toggleHidden}
                onMove={move}
              />
            ) : (
              <S.Hint text="No layers were found in this size." />
            )}
            {siblingSizes.length > 0 ? (
              <S.Hint
                text="Sizes of the same shape change with it: {{sizes}}."
                placeholders={{ sizes: siblingSizes.join(', ') }}
              />
            ) : null}
          </S.Section>

          {selected ? (
            <>
              <S.Section>
                <S.SectionTitle raw text={role} />
                <S.ControlLabel text="Size · {{n}}%" placeholders={{ n: Math.round(scale * 100) }} />
                <S.Range
                  min={Math.round(MIN_LAYER_SCALE * 100)}
                  max={Math.round(MAX_LAYER_SCALE * 100)}
                  step={5}
                  value={Math.round(scale * 100)}
                  onChange={(e) => setTransform(selected.id, { scale: clampLayerScale(Number(e.target.value) / 100) })}
                  disabled={locked || selected.transform.hidden}
                  aria-label={t('Size of the {{layer}} layer', { layer: role })}
                />
                <S.ControlRow>
                  <Button
                    variant="outline"
                    icon={selected.transform.hidden ? 'eye' : 'eye-off'}
                    label={selected.transform.hidden ? t('Show') : t('Hide')}
                    onClick={() => toggleHidden(selected.id)}
                    disabled={locked}
                  />
                  <Button
                    variant="text"
                    icon="refresh"
                    label={t('Reset position')}
                    onClick={() => setTransform(selected.id, { dx: 0, dy: 0, scale: 1 })}
                    disabled={locked || untouched}
                  />
                </S.ControlRow>
              </S.Section>

              {isRestylableRole(selected.role) ? (
                <S.Section>
                  <S.SectionTitle text="Restyle" />
                  <TextArea
                    label={t('How should it look?')}
                    value={restyleDraft}
                    onChange={setRestyleDraft}
                    placeholder={t('For example: make it gold with a soft glow.')}
                    rows={2}
                    disabled={locked}
                  />
                  <GenerateButton
                    compact
                    label={t('Restyle')}
                    cost={editCost}
                    disabled={locked || !restyleDraft.trim()}
                    onClick={() => void runEdit('restyle')}
                  />
                </S.Section>
              ) : null}
            </>
          ) : (
            <S.Hint text="Select a layer on the canvas or in the list to move, resize, hide or rewrite it." />
          )}
        </S.Panel>
      </S.Body>
    </Sheet>
  );
};

export default KitLayerEditor;
